import React from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import Home from "./pages/Home";
import Services from "./pages/Services";
import AboutUs from "./pages/AboutUs";
import ContactUs from "./pages/ContactUs";
import MaintenancePage from "./pages/MaintenancePage";
import { usePageMeta } from "./hooks/usePageMeta";
import { useScrollToTop } from "./hooks/useScrollToTop";

// Maintenance Mode Toggle
// Set to true to show maintenance page, false to show normal website
const MAINTENANCE_MODE = process.env.REACT_APP_MAINTENANCE_MODE === "false";

// Meta data for each page
const pageMetaData = {
  home: {
    title: "Postmint - Web Development & Design Services",
    description:
      "Professional web development, design, and digital marketing services. Transform your business online.",
    keywords: "web development, web design, digital services",
    canonical: "https://postmint.com/",
  },
  services: {
    title: "Our Services - Postmint",
    description:
      "Explore our comprehensive web development, design, and digital marketing services tailored to your business needs.",
    keywords:
      "web services, web development, design services, digital marketing",
    canonical: "https://postmint.com/services",
  },
  aboutUs: {
    title: "About Us - Postmint",
    description:
      "Learn about Postmint, our mission, and the team behind our successful projects.",
    keywords: "about us, company, team, mission",
    canonical: "https://postmint.com/about-us",
  },
  contactUs: {
    title: "Contact Us - Postmint",
    description:
      "Get in touch with Postmint. We'd love to hear from you and discuss your project needs.",
    keywords: "contact, contact us, get in touch",
    canonical: "https://postmint.com/contact-us",
  },
  maintenance: {
    title: "Maintenance - Postmint",
    description:
      "We're currently performing scheduled maintenance. We'll be back online shortly.",
    keywords: "maintenance, scheduled, maintenance mode",
    canonical: "https://postmint.com/",
  },
};

// Page component wrapper with meta data
function PageWrapper({ component: Component, meta }) {
  usePageMeta(meta);
  return <Component />;
}

function AppContent() {
  // Scroll to top whenever route changes
  useScrollToTop();

  // If maintenance mode is enabled, show only the maintenance page
  if (MAINTENANCE_MODE) {
    return (
      <div className="bg-white flex flex-col min-h-screen">
        <PageWrapper
          component={MaintenancePage}
          meta={pageMetaData.maintenance}
        />
      </div>
    );
  }

  // Otherwise, show normal website
  return (
    <div className="bg-white flex flex-col min-h-screen">
      <Navbar />
      <main className="flex-grow">
        <Routes>
          <Route
            path="/"
            element={<PageWrapper component={Home} meta={pageMetaData.home} />}
          />
          <Route
            path="/services"
            element={
              <PageWrapper component={Services} meta={pageMetaData.services} />
            }
          />
          <Route
            path="/about-us"
            element={
              <PageWrapper component={AboutUs} meta={pageMetaData.aboutUs} />
            }
          />
          <Route
            path="/contact-us"
            element={
              <PageWrapper
                component={ContactUs}
                meta={pageMetaData.contactUs}
              />
            }
          />
          <Route
            path="*"
            element={<PageWrapper component={Home} meta={pageMetaData.home} />}
          />
        </Routes>
      </main>
      <Footer />
    </div>
  );
}

function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default App;
